import { action, computed, observable, reaction, runInAction } from 'mobx';
import { toast } from 'react-toastify';

import agent from '../api/agent';
import { addDay, formatParameterDisp2, RESERVATION_STATE } from '../common/util/util';
import { IOption } from '../models/option';
import { IReservation, IReservationFilters } from '../models/reservation';
import { RootStore } from './rootStore';

export default class ReservationStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.reservationFilters,
      () => {
        this.scheduleRegistry.clear();
        this.loadReservations();
      }
    );
  }

  @observable scheduleRegistry = new Map();
  @observable
  reservationFilters: IReservationFilters | null = null;
  @observable scheduleOptions: IOption[] = [];

  @observable reservation: IReservation | null = null;
  @observable sysdate: Date | null = null;
  @observable loading = false;
  @observable loading2 = false;
  @observable submitting = false;

  @computed get schedules() {
    return Array.from(this.scheduleRegistry.values());
  }

  @computed get reservationPeriod() {
    return addDay(this.sysdate!, 90);
  }

  @action setReservationFilters = (reservationFilters: IReservationFilters) => {
    this.reservationFilters = reservationFilters;
  };

  @action getSysDate = async () => {
    try {
      const sysdate = await agent.Reservations.sysdate();
      runInAction('get SysDate', () => {
        this.sysdate = new Date(sysdate);
      });
    } catch (error) {
      runInAction('get SysDate error', () => {
      });
    }
  };

  @action loadReservation = async (qr: string) => {
    this.loading = true;
    try {
      const reservation = await agent.Reservations.checkQrCode(qr);
      runInAction('loading reservation', () => {
        reservation.startDate = new Date(reservation.startDate!);
        reservation.limitMonthDate = new Date(reservation.limitMonthDate!);
        this.reservation = reservation;
        this.loading = false;
      });
    } catch (error) {
      runInAction('load reservation error', () => {
        this.loading = false;
      });
    }
  };

  @action deleteReservation = async () => {
    this.submitting = true;
    try {
      const reservation = await agent.Reservations.delete(
        this.reservation?.qrticketId!
      );
      runInAction('deleting reservation', () => {
        reservation.startDate = new Date(reservation.startDate!);
        reservation.limitMonthDate = new Date(reservation.limitMonthDate!);
        this.reservation = reservation;
        this.submitting = false;
      });
      this.scheduleRegistry.clear();
    } catch (error) {
      runInAction('delete reservation error', () => {
        this.submitting = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action createReservation = async (reservation: IReservation) => {
    this.submitting = true;
    try {
      const reservation2 = await agent.Reservations.create(reservation);
      runInAction('creating reservation', () => {
        this.reservation = reservation2;
        this.submitting = false;
      });
      this.scheduleRegistry.clear();
    } catch (error) {
      runInAction('create reservation error', () => {
        this.submitting = false;
      });
      error.response &&
        error.response.data &&
        error.response.data.errors &&
        toast.error(Object.values(error.response.data.errors).flat().join());
    }
  };

  @action loadReservations = async () => {
    this.loading2 = true;
    try {
      const reservations = await agent.Reservations.list(
        formatParameterDisp2(this.reservationFilters?.reservationDate!)
      );
      runInAction('loading reservations', () => {
        reservations.schedules.forEach((s) => {
          this.scheduleRegistry.set(s.schedule, s);
        });
        this.scheduleOptions = reservations.schedules
          .filter((s) => s.status !== RESERVATION_STATE.Danger)
          .map((s) => ({
            key: s.schedule,
            value: s.schedule,
            text: s.schedule,
          }));
        this.loading2 = false;
      });
    } catch (error) {
      runInAction('load reservations error', () => {
        this.loading2 = false;
      });
    }
  };
}
